import type { InvalidCount } from 'components/common-form/hooks/invalid-fields/model';
import type { FC } from 'react';

import { CountBadge } from './CountBadge';

type FieldCountBadgesProps = {
  id: string;
  value: InvalidCount;
  modifiedCount: number;
  errorPrefix?: string;
};

/**
 * Renders validation and modified-field badges for a tab or block.
 *
 * This keeps the current aggregate validation behavior: when any validation error exists, the whole
 * validation count is shown in orange. When only validation warnings exist, validation warnings share
 * the blue info badge with modified (patched) fields.
 *
 * Modified fields are counted separately and never turn the validation badge orange.
 */
export const FieldCountBadges: FC<FieldCountBadgesProps> = ({ id, value, modifiedCount, errorPrefix }) => {
  const { totalCount, severity, validations } = value;
  const hasValidationError = severity === 'error';
  const orangeCount = hasValidationError ? totalCount : 0;
  const infoValidationCount = hasValidationError ? 0 : totalCount;
  const blueCount = infoValidationCount + modifiedCount;
  const infoValidations = hasValidationError ? [] : validations;

  return (
    <>
      <CountBadge
        id={`error-count-${id}`}
        count={orangeCount}
        color="warning"
        validations={validations}
        errorPrefix={errorPrefix}
      />
      <CountBadge
        id={`info-count-${id}`}
        count={blueCount}
        color="info"
        validations={infoValidations}
        errorPrefix={errorPrefix}
      />
    </>
  );
};
